import assertNever from "assert-never";
import DOMPurify from "dompurify";
import * as React from "react";
import * as Ons from "react-onsenui";
import { isLoading, Loading } from "../Loading";
import { RecruitmentCaregiverApplicationQuestionId } from "../messages/ObjectIds";
import {
  DEFAULT_TRANSLATION_LANGUAGE,
  PossibleAnswer,
  RecruitmentQuestionData,
} from "../messages/Recruitment";
import { OrientationLanguages, TranslationMapping } from "../messages/Video";
import { STRINGS } from "../strings";
import { AppCaregiverState } from "../system/AppState/AppCaregiverState";
import { translate } from "../translate";
import { debounce } from "../utils/Debounce";
import QuestionHeader from "./QuestionHeader";
import { RequiredDocMainHeader } from "./ScheduledVisitDocScreen";
import { ScreenSkeleton } from "./ScreenSkeleton";
import { Signature } from "./Signture";

interface RecruitmentFormScreenProps {
  onBackClick: () => void;
  onAnswer: (
    questionId: RecruitmentCaregiverApplicationQuestionId,
    answer: string | number | number[] | null
  ) => void;
  onPicUpload: (questionId: RecruitmentCaregiverApplicationQuestionId) => void;
  onSubmit: () => void;
  confirmFinish: () => void;
  recruitmentForm: RecruitmentQuestionData[] | Loading;
  language: OrientationLanguages | null;
  showMadatoryProblems: boolean;
  awaitingSubmit: boolean;
  showModal: boolean;
  caregiver: AppCaregiverState | Loading;
  onHideModal: () => void;
}

export class RecruitmentFormScreen extends React.Component<RecruitmentFormScreenProps> {
  getTranslatedInfoFromQuestion(question: RecruitmentQuestionData, language: OrientationLanguages) {
    const text = extractLanguage(question.questionText, language);
    if (
      (question.type === "Radio" || question.type === "Checkbox") &&
      question.possibleAnswers === null
    ) {
      throw new Error(
        "Radio and CheckBox don't have possible answers. Question Id: " + question.id
      );
    }
    const possibleAnswers =
      question.possibleAnswers === null
        ? null
        : extractLanguage(question.possibleAnswers, language);
    return { text: text, possibleAnswers: possibleAnswers };
  }

  handleQuestionAnswer = debounce(
    (
      questionId: RecruitmentCaregiverApplicationQuestionId,
      answer: string | number | number[] | null,
      forceUpdate?: boolean
    ) => {
      this.props.onAnswer(questionId, answer);
      if (forceUpdate === true || this.questionIsParent(questionId)) {
        this.forceUpdate();
      }
    },
    300
  );

  questionIsParent = (id: RecruitmentCaregiverApplicationQuestionId) => {
    if (isLoading(this.props.recruitmentForm)) {
      throw new Error("The impossible happened");
    }

    return this.props.recruitmentForm.some((val) =>
      val.parentId === null
        ? false
        : RecruitmentCaregiverApplicationQuestionId.unwrap(val.parentId) ===
          RecruitmentCaregiverApplicationQuestionId.unwrap(id)
    );
  };

  getQuestionById(id: RecruitmentCaregiverApplicationQuestionId) {
    const questionId = RecruitmentCaregiverApplicationQuestionId.unwrap(id);
    if (isLoading(this.props.recruitmentForm)) {
      throw new Error("The impossible happened");
    }
    const question: RecruitmentQuestionData[] = this.props.recruitmentForm.filter((val) => {
      return RecruitmentCaregiverApplicationQuestionId.unwrap(val.id) === questionId;
    });
    if (question.length !== 1) {
      throw new Error(`Couldn't find element ${questionId} in questions form`);
    }
    return question[0];
  }

  shouldRender(question: RecruitmentQuestionData) {
    if (question.parentId === null) {
      return true;
    }

    const parentQuestion = this.getQuestionById(question.parentId);
    if (parentQuestion.answer === null) {
      return false;
    }
    if (typeof parentQuestion.answer !== "number") {
      throw new Error(
        `Unsupported answer type on parent ${parentQuestion.id} for child ${question.id}`
      );
    }

    return parentQuestion.answer === question.parentAnswer;
  }

  getCssClassForQuestion(question: RecruitmentQuestionData) {
    if (question.type === "TitleText") {
      return "RecruitmentFormScreen__basicMargin";
    }
    return (
      "RecruitmentFormScreen__questionContainer " +
      (question.isMandatory
        ? "RecruitmentFormScreen__questionContainerMandatory " +
          (this.props.showMadatoryProblems && question.answer === null
            ? "RecruitmentFormScreen__questionContainerMandatoryActive"
            : "")
        : "")
    );
  }

  getQuestionAnswer(question: RecruitmentQuestionData) {
    const errorMessage = `Question ${question.id} has a bad answer`;

    switch (question.type) {
      case "Text":
      case "Document":
      case "Email":
      case "FullDate":
      case "LongText":
      case "MonthYearDate":
      case "Signature":
        if (question.answer !== null && typeof question.answer !== "string") {
          throw new Error(errorMessage);
        }
        return question.answer;
      case "Radio":
      case "Numeric":
        if (question.answer !== null && typeof question.answer !== "number") {
          throw new Error(errorMessage);
        }
        return question.answer;
      case "Checkbox":
        if (question.answer !== null && !Array.isArray(question.answer)) {
          throw new Error(errorMessage);
        }
        return question.answer;
      case "TitleText":
        return null;
      default:
        return assertNever(question.type);
    }
  }

  renderQuestion = (question: RecruitmentQuestionData) => {
    if (this.props.language === null) {
      // This is called when the language was already loaded from the device
      throw new Error("The impossible happened");
    }

    if (!this.shouldRender(question)) {
      return null;
    }

    const { text, possibleAnswers } = this.getTranslatedInfoFromQuestion(
      question,
      this.props.language
    );
    const questionAnswer = this.getQuestionAnswer(question);
    let component = (
      <QuestionHeader
        isRequired={question.isMandatory}
        text={text}
        notes={null}
        isReadonly={false}
      />
    );
    switch (question.type) {
      case "Checkbox":
        component = (
          <RecruitmentCheckbox
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            possibleAnswers={possibleAnswers as PossibleAnswer[]}
            answer={questionAnswer as number[]}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
            required={question.isMandatory}
          />
        );
        break;
      case "Document":
        component = (
          <RecruitmentDocument
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            onUploadClick={() => this.props.onPicUpload(question.id)}
            answer={questionAnswer as string}
            onRemove={(id) => {
              this.handleQuestionAnswer(id, null, true);
            }}
          />
        );
        break;
      case "Email":
        component = (
          <RecruitmentEmail
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            answer={questionAnswer as string}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
            required={question.isMandatory}
          />
        );
        break;
      case "FullDate":
        component = (
          <RecruitmentDate
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            answer={questionAnswer as string}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
            required={question.isMandatory}
          />
        );
        break;
      case "MonthYearDate":
        component = (
          <RecruitmentDate
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            answer={questionAnswer as string}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
            required={question.isMandatory}
            type={"month"}
          />
        );
        break;
      case "Numeric":
        component = (
          <RecruitmentNumeric
            required={question.isMandatory}
            answer={questionAnswer as number}
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
          />
        );
        break;
      case "Radio":
        component = (
          <RecruitmentRadio
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            possibleAnswers={possibleAnswers as PossibleAnswer[]}
            answer={questionAnswer as number}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
            required={question.isMandatory}
          />
        );
        break;
      case "Signature":
        component = (
          <RecruitmentSignature
            required={question.isMandatory}
            answer={questionAnswer as string}
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
          />
        );
        break;
      case "Text":
        component = (
          <RecruitmentText
            required={question.isMandatory}
            answer={questionAnswer as string}
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
          />
        );
        break;
      case "TitleText":
        component = <RequiredDocMainHeader text={text} withoutSeperator={true} />;
        break;
      case "LongText":
        component = (
          <RecruitmentLongText
            required={question.isMandatory}
            answer={questionAnswer as string}
            key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)}
            id={question.id}
            text={text}
            onChange={(id, answer) => this.handleQuestionAnswer(id, answer)}
            placeholder={"Enter text here"}
          />
        );
        break;
      default:
        assertNever(question.type);
    }

    const cssClass = this.getCssClassForQuestion(question);

    return (
      <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(question.id)} className={cssClass}>
        {component}
      </div>
    );
  };

  render() {
    return (
      <ScreenSkeleton
        title={"Recruitment Application"}
        titleBarStyle={ScreenSkeleton.TitleBarStyle.PRIMARY}
        onBackClick={this.props.onBackClick}
      >
        <RecruitmewntSuccessModal
          onHideModal={this.props.onHideModal}
          isModalOpen={this.props.showModal}
          onConfirm={this.props.confirmFinish}
        />
        {this.props.language === null || isLoading(this.props.recruitmentForm) ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Ons.ProgressCircular indeterminate={true} />
          </div>
        ) : (
          <div>
            <div className={"RecruitmentFormScreen__headline"}>
              <p>
                Hi {isLoading(this.props.caregiver) ? "" : this.props.caregiver.caregiver.firstName}
                , Welcome to MedFlyt.
                <br />
                Please answer the following questions and upload the required documents.
              </p>
              <p>
                Complete the application and we will connect you with agencies that will offer you
                work right away.
              </p>
              <p>Please note - you can calways continue this application at a later time.</p>
              <h1>Good Luck !</h1>
            </div>
            {this.props.recruitmentForm.map(this.renderQuestion)}
            <div className="RecruitmentFormScreen__controllers">
              {this.props.awaitingSubmit ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Ons.ProgressCircular indeterminate={true} />
                </div>
              ) : (
                <Ons.Button
                  className={"RecruitmentFormScreen__button"}
                  modifier={"material large"}
                  onClick={this.props.onSubmit}
                >
                  {translate(STRINGS.SUBMIT)}
                </Ons.Button>
              )}
              <Ons.Button
                className={"RecruitmentFormScreen__later"}
                modifier={"large--quiet"}
                onClick={this.props.onBackClick}
              >
                {translate(STRINGS.CONTINUE_LATER)}
              </Ons.Button>
            </div>
          </div>
        )}
      </ScreenSkeleton>
    );
  }
}

class RecruitmentSignature extends React.Component<
  BasicQuestionInfo<string>,
  { value: string | null }
> {
  constructor(props: BasicQuestionInfo<string>) {
    super(props);
    this.state = {
      value: props.answer,
    };
  }

  onChange(answer: string | null) {
    this.setState({
      value: answer,
    });
    this.props.onChange(this.props.id, answer);
  }

  render() {
    return (
      <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(this.props.id)}>
        {/* <QuestionHeader notes={null} text={this.props.text} isRequired={this.props.required} /> */}
        {this.props.required ? <span style={{ color: "red" }}>*</span> : null}
        <div
          className={"RecruitmentSignature__header"}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(this.props.text) }}
        />
        <div className={"RecruitmentSignature__container"}>
          <Signature onEnd={(data) => this.onChange(data)} initialData={this.state.value} />
        </div>
      </div>
    );
  }
}

interface RecruitmentDateProps extends BasicQuestionInfo<string> {
  type?: "date" | "month";
}
class RecruitmentDate extends React.Component<RecruitmentDateProps, { value: string }> {
  constructor(props: RecruitmentDateProps) {
    super(props);
    this.state = {
      value: props.answer === null ? "" : props.answer,
    };
  }

  onChange(id: RecruitmentCaregiverApplicationQuestionId, answer: string) {
    this.setState({ value: answer });
    this.props.onChange(id, answer.trim() === "" ? null : answer.trim());
  }

  render() {
    return (
      <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(this.props.id)}>
        <QuestionHeader
          notes={null}
          text={this.props.text}
          isRequired={this.props.required}
          isReadonly={false}
        />
        <div className={"RecruitmentDate__container"}>
          <input
            className={"RecruitmentDate__datePicker"}
            type={this.props.type === undefined ? "date" : this.props.type}
            value={this.state.value}
            onChange={(event) => this.onChange(this.props.id, event.target.value)}
          />
        </div>
      </div>
    );
  }
}

interface RecruitmentTextProps extends BasicQuestionInfo<string> {
  type?: string;
  placeholder?: string;
}
interface RecruitmentTextState {
  value: string;
}
class RecruitmentText extends React.Component<RecruitmentTextProps, RecruitmentTextState> {
  constructor(props: RecruitmentTextProps) {
    super(props);
    this.state = {
      value: props.answer === null ? "" : props.answer,
    };
  }

  handleAnswer = (id: RecruitmentCaregiverApplicationQuestionId, answer: string) => {
    const finalAnswer = answer.trim() === "" ? null : answer;
    this.setState({ value: finalAnswer === null ? "" : finalAnswer });
    this.props.onChange(id, finalAnswer);
  };
  render() {
    return (
      <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(this.props.id)}>
        <QuestionHeader
          notes={null}
          text={this.props.text}
          isRequired={this.props.required}
          isReadonly={false}
        />
        <div className="RecruitmentFormScreen__textBlockContainer">
          <input
            className="RecruitmentFormScreen__textBlock"
            type={this.props.type === undefined ? "text" : this.props.type}
            value={this.state.value}
            onChange={(event) => this.handleAnswer(this.props.id, event.target.value)}
            placeholder={this.props.placeholder === undefined ? "" : this.props.placeholder}
          />
        </div>
      </div>
    );
  }
}

function RecruitmentEmail(props: RecruitmentTextProps) {
  return <RecruitmentText {...props} type={"email"} />;
}

class RecruitmentLongText extends React.Component<RecruitmentTextProps, RecruitmentTextState> {
  constructor(props: RecruitmentTextProps) {
    super(props);
    this.state = {
      value: props.answer === null ? "" : props.answer,
    };
  }

  handleAnswer = (id: RecruitmentCaregiverApplicationQuestionId, answer: string) => {
    const finalAnswer = answer.trim() === "" ? null : answer;
    this.setState({ value: finalAnswer === null ? "" : finalAnswer });
    this.props.onChange(id, finalAnswer);
  };

  render() {
    return (
      <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(this.props.id)}>
        <QuestionHeader
          notes={null}
          text={this.props.text}
          isRequired={this.props.required}
          isReadonly={false}
        />
        <div className="RecruitmentFormScreen__textBlockContainer">
          <textarea
            className="RecruitmentFormScreen__textBlock RecruitmentFormScreen__textarea"
            value={this.state.value}
            rows={12}
            onChange={(event) => this.handleAnswer(this.props.id, event.target.value)}
            placeholder={this.props.placeholder === undefined ? "" : this.props.placeholder}
          />
        </div>
      </div>
    );
  }
}

interface RecruitmentRadioProps extends RecruitmentMultiQuestionProps<number> {}

class RecruitmentRadio extends React.Component<RecruitmentRadioProps, RecruitmentNumericState> {
  constructor(props: RecruitmentRadioProps) {
    super(props);
    this.state = {
      value: props.answer,
    };
  }

  onChange(id: RecruitmentCaregiverApplicationQuestionId, answer: number) {
    this.setState({ value: answer });
    this.props.onChange(id, answer);
  }
  render() {
    return (
      <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(this.props.id)}>
        <QuestionHeader
          text={this.props.text}
          isRequired={this.props.required}
          notes={null}
          isReadonly={false}
        />
        <div
          className="RecruitmentRadio__toggle"
          style={{ flexFlow: this.props.possibleAnswers.length < 3 ? "row" : "column" }}
        >
          {this.props.possibleAnswers.map((val) => {
            return (
              <div key={val.id} className={"RecruitmentRadio__checkContainer"}>
                <input
                  type={"radio"}
                  name={`radio-${this.props.id}`}
                  id={`radio-${this.props.id}-${val.id}`}
                  checked={this.state.value === val.id}
                  onChange={() => this.onChange(this.props.id, val.id)}
                />
                <label htmlFor={`radio-${this.props.id}-${val.id}`}>{val.text}</label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

interface RecruitmentCheckboxProps extends RecruitmentMultiQuestionProps<number[]> {}

class RecruitmentCheckbox extends React.Component<
  RecruitmentCheckboxProps,
  { value: number[] | null }
> {
  constructor(props: RecruitmentCheckboxProps) {
    super(props);
    this.state = {
      value: props.answer,
    };
  }

  handleCheckbox = (
    id: RecruitmentCaregiverApplicationQuestionId,
    answer: number,
    checked: boolean
  ) => {
    let checkboxAnswer = this.state.value === null ? [] : this.state.value;
    if (checked) {
      checkboxAnswer.push(answer);
    } else {
      checkboxAnswer = checkboxAnswer.filter((val) => val !== answer);
    }
    if (checkboxAnswer.length === 0) {
      this.setState({ value: null });
      this.props.onChange(id, null);
    } else {
      this.setState({
        value: [...checkboxAnswer],
      });
      this.props.onChange(id, checkboxAnswer);
    }
  };

  render() {
    return (
      <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(this.props.id)}>
        <QuestionHeader
          text={this.props.text}
          isRequired={this.props.required}
          notes={null}
          isReadonly={false}
        />
        <div
          className="RecruitmentRadio__toggle"
          style={{ flexFlow: this.props.possibleAnswers.length < 3 ? "row" : "column" }}
        >
          {this.props.possibleAnswers.map((val) => {
            const checked =
              this.state.value === null ? false : this.state.value.some((a) => a === val.id);
            return (
              <div key={val.id} className={"RecruitmentRadio__checkContainer"}>
                <input
                  type={"checkbox"}
                  name={`checkbox-${this.props.id}`}
                  id={`checkbox-${this.props.id}-${val.id}`}
                  checked={checked}
                  onChange={(event) =>
                    this.handleCheckbox(this.props.id, val.id, event.target.checked)
                  }
                />
                <label htmlFor={`checkbox-${this.props.id}-${val.id}`}>{val.text}</label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

interface RecruitmentNumericProps extends BasicQuestionInfo<number> {}
interface RecruitmentNumericState {
  value: string | number | null;
}
class RecruitmentNumeric extends React.Component<RecruitmentNumericProps, RecruitmentNumericState> {
  constructor(props: RecruitmentNumericProps) {
    super(props);
    this.state = {
      value: props.answer === null ? "" : props.answer,
    };
  }

  shouldComponentUpdate(_nextProps: RecruitmentNumericProps, nextState: RecruitmentNumericState) {
    return nextState.value !== this.state.value;
  }

  handleAnswer = (id: RecruitmentCaregiverApplicationQuestionId, answer: string) => {
    let fAnswer: number | null = parseInt(answer, 10);

    if (isNaN(fAnswer)) {
      fAnswer = null;
    }
    this.props.onChange(id, fAnswer);
    this.setState({
      value: fAnswer,
    });
    console.log(fAnswer);
  };

  render() {
    return (
      <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(this.props.id)}>
        <QuestionHeader
          text={this.props.text}
          isRequired={this.props.required}
          notes={null}
          isReadonly={false}
        />
        <div className={"RecruitmentFormScreen__textBlockContainer"}>
          <input
            type={"number"}
            pattern="\\d*"
            className={"RecruitmentFormScreen__textBlock"}
            value={this.state.value === null ? "" : this.state.value}
            onChange={(event) => this.handleAnswer(this.props.id, event.target.value)}
          />
        </div>
      </div>
    );
  }
}

function RecruitmentDocument(props: {
  id: RecruitmentCaregiverApplicationQuestionId;
  text: string;
  answer: string | null;
  onUploadClick: () => void;
  onRemove: (id: RecruitmentCaregiverApplicationQuestionId) => void;
}) {
  const img = (
    <img
      // crossOrigin={"Anonymous"}
      className={"RecruitmentDocument__image"}
      id={`myImage-${RecruitmentCaregiverApplicationQuestionId.unwrap(props.id)}`}
      src={props.answer === null ? "" : props.answer}
      alt={""}
      onClick={props.onUploadClick}
    />
  );
  return (
    <div key={RecruitmentCaregiverApplicationQuestionId.unwrap(props.id)}>
      <QuestionHeader text={props.text} isRequired={true} notes={null} isReadonly={false} />
      <div className="RecruitmentDocument__imageContainer">{img}</div>
      {props.answer === null ? (
        <div className={"RecruitmentDocument__controllersContainer"}>
          <Ons.Button
            className={"RecruitmentFormScreen__button"}
            modifier={"large--cta"}
            onClick={props.onUploadClick}
          >
            {translate(STRINGS.TAKE_A_PHOTO)}
          </Ons.Button>
        </div>
      ) : (
        <Ons.Button
          className={"RecruitmentDocument__removeButton RecruitmentFormScreen__button"}
          modifier={"cta"}
          onClick={() => props.onRemove(props.id)}
        >
          {translate(STRINGS.REMOVE)}
        </Ons.Button>
      )}
    </div>
  );
}

type AnswerTypes = string | number | number[];

interface BasicQuestionInfo<T extends AnswerTypes> {
  id: RecruitmentCaregiverApplicationQuestionId;
  text: string;
  answer: T | null;
  required: boolean;
  onChange: (questionId: RecruitmentCaregiverApplicationQuestionId, answer: T | null) => void;
}

interface RecruitmentMultiQuestionProps<T extends number | number[]> extends BasicQuestionInfo<T> {
  possibleAnswers: PossibleAnswer[];
}

export function RecruitmewntSuccessModal(props: {
  onHideModal: () => void;
  onConfirm: () => void;
  isModalOpen: boolean;
}) {
  return (
    <Ons.Modal isOpen={props.isModalOpen} onDeviceBackButton={props.onHideModal} animation={"fade"}>
      <div className={"RecruitmentFormScreen__dialogRoot"}>
        <div className={"RecruitmentFormScreen__modalTitle"}>
          {translate(STRINGS.APPLICATION_DONE)}
          <br />
          {translate(STRINGS.GREAT_JOB)}
        </div>
        <div className={"RecruitmentFormScreen__modalBody"}>
          <div className={"RecruitmentFormScreen__modalIcon"} />
          <div className={"RecruitmentFormScreen__modalText"}>
            {translate(STRINGS.AGENCIES_CONTACT)}
          </div>
        </div>
        <div
          className={"RecruitmentFormScreen__modalButton"}
          onClick={() => {
            props.onHideModal();
            props.onConfirm();
          }}
        >
          {translate(STRINGS.GOT_IT)}
        </div>
      </div>
    </Ons.Modal>
  );
}

export function extractLanguage<T>(
  obj: TranslationMapping<T>,
  language: OrientationLanguages,
  def?: OrientationLanguages
): T {
  let info = obj[language];
  if (info === undefined) {
    if (def === undefined) {
      info = obj[DEFAULT_TRANSLATION_LANGUAGE];
    } else {
      info = obj[def];
    }

    if (info === undefined) {
      throw new Error("Translation object doesn't contain the default fallback language");
    }
  }
  return info;
}
